/**
 * HR Engine - Page Reporting RH Performance
 * Tableau de bord analytique pour les RH
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  BarChart3,
  Target,
  Users,
  Award,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Star,
  Building2,
  Calendar,
  Loader2,
  RefreshCw,
  Download,
  ChevronRight,
  Eye,
  ArrowLeft,
  PieChart,
  Activity
} from 'lucide-react';

// Performance level configuration
const PERFORMANCE_LEVELS = {
  high_performer: { 
    label: 'High Performer', 
    color: 'bg-emerald-100 text-emerald-700', 
    bgLight: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: Star,
    iconColor: 'text-emerald-600'
  },
  performer: { 
    label: 'Performer', 
    color: 'bg-blue-100 text-blue-700',
    bgLight: 'bg-blue-50',
    border: 'border-blue-200',
    icon: TrendingUp,
    iconColor: 'text-blue-600'
  },
  non_performer: { 
    label: 'Non Performer', 
    color: 'bg-red-100 text-red-700',
    bgLight: 'bg-red-50',
    border: 'border-red-200',
    icon: AlertTriangle,
    iconColor: 'text-red-600'
  }
};

// Score distribution bar component
const ScoreDistributionBar = ({ distribution }) => {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const ranges = [
    { key: '80-100', label: '80-100', color: 'bg-emerald-500' },
    { key: '60-80', label: '60-80', color: 'bg-blue-500' },
    { key: '40-60', label: '40-60', color: 'bg-amber-500' },
    { key: '20-40', label: '20-40', color: 'bg-orange-500' },
    { key: '0-20', label: '0-20', color: 'bg-red-500' }
  ];

  return (
    <div className="space-y-2">
      <div className="flex h-4 rounded-full overflow-hidden bg-slate-100">
        {ranges.map(range => {
          const count = distribution[range.key] || 0;
          const percent = (count / total) * 100;
          if (percent === 0) return null;
          return (
            <div
              key={range.key}
              className={`${range.color} transition-all`}
              style={{ width: `${percent}%` }}
              title={`${range.label}: ${count} (${percent.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-slate-500">
        {ranges.map(range => (
          <div key={range.key} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${range.color}`} />
            <span>{range.label}: {distribution[range.key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Employee performance card
const EmployeePerformanceCard = ({ employee, level, onClick }) => {
  const config = PERFORMANCE_LEVELS[level];
  const Icon = config?.icon || Star;

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:shadow-md transition-all ${config?.bgLight} ${config?.border} border`}
      onClick={onClick}
    >
      <Avatar className="h-10 w-10">
        <AvatarFallback className={config?.color}>
          {employee.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{employee.name}</p>
        <p className="text-sm text-slate-500 truncate">{employee.jobTitle || 'Poste non défini'}</p>
      </div>
      <div className="text-right">
        <p className={`text-lg font-bold ${config?.iconColor}`}>{employee.score}</p>
        <p className="text-xs text-slate-500">/100</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </div>
  );
};

// Department card
const DepartmentCard = ({ dept }) => {
  const performanceLevel = dept.avgScore >= 85 ? 'high_performer' : 
                           dept.avgScore >= 60 ? 'performer' : 'non_performer';
  const config = PERFORMANCE_LEVELS[performanceLevel];

  return (
    <Card className={`${config?.bgLight} ${config?.border}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 className={`w-5 h-5 ${config?.iconColor}`} />
            <h3 className="font-semibold text-slate-900">{dept.departmentName}</h3>
          </div>
          <Badge className={config?.color}>{dept.avgScore}</Badge>
        </div>
        
        <div className="grid grid-cols-4 gap-2 text-center text-sm">
          <div>
            <p className="font-bold text-slate-900">{dept.employeeCount}</p>
            <p className="text-xs text-slate-500">Employés</p>
          </div>
          <div>
            <p className="font-bold text-emerald-600">{dept.highPerformers}</p>
            <p className="text-xs text-slate-500">High</p>
          </div>
          <div>
            <p className="font-bold text-blue-600">{dept.performers}</p>
            <p className="text-xs text-slate-500">Perf.</p>
          </div>
          <div>
            <p className="font-bold text-red-600">{dept.nonPerformers}</p>
            <p className="text-xs text-slate-500">Non</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PerformanceReportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      fetchReport(selectedCampaign);
    }
  }, [selectedCampaign]);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/performance-eval/campaigns');
      const allCampaigns = response.data.campaigns || [];
      setCampaigns(allCampaigns);
      
      // Select first active or completed campaign
      const activeCampaign = allCampaigns.find(c => 
        ['active', 'self_eval', 'manager_eval', 'validation', 'completed'].includes(c.status)
      );
      if (activeCampaign) {
        setSelectedCampaign(activeCampaign.id);
      } else if (allCampaigns.length > 0) {
        setSelectedCampaign(allCampaigns[0].id);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Erreur lors du chargement des campagnes');
    }
  };

  const fetchReport = async (campaignId) => {
    try {
      setLoading(true);
      const response = await api.get(`/performance-eval/reports/overview?campaign_id=${campaignId}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (selectedCampaign) {
      await fetchReport(selectedCampaign);
      toast.success('Données actualisées');
    }
  };

  const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);

  if (loading && !reportData) {
    return (
      <DashboardLayout title="Reporting RH">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  const summary = reportData?.summary || {};
  const perfLevels = reportData?.performanceLevels || {};
  const byDepartment = reportData?.byDepartment || [];
  const scoreDistribution = reportData?.scoreDistribution || {};

  return (
    <DashboardLayout title="Reporting RH">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              className="mb-2 -ml-2"
              onClick={() => navigate('/evaluation-campaigns')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux campagnes
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-indigo-500" />
              Reporting Performance RH
            </h1>
            <p className="text-slate-500 mt-1">
              Analyse de la performance par employé, département et site
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="Sélectionner une campagne" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" disabled>
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>

        {/* Campaign info */}
        {selectedCampaignData && (
          <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Target className="w-8 h-8 text-indigo-600" />
                  <div>
                    <h2 className="font-semibold text-lg text-slate-900">
                      {selectedCampaignData.name}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4" />
                      {new Date(selectedCampaignData.periodStart).toLocaleDateString('fr-FR')} - 
                      {new Date(selectedCampaignData.periodEnd).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{summary.totalEvaluations || 0}</p>
                    <p className="text-xs text-slate-500">Évaluations</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{summary.completedEvaluations || 0}</p>
                    <p className="text-xs text-slate-500">Complétées</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-600">{summary.avgScore || '-'}</p>
                    <p className="text-xs text-slate-500">Score moyen</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-emerald-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Star className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{perfLevels.highPerformers?.count || 0}</p>
                <p className="text-sm text-slate-500">High Performers</p>
                <p className="text-xs text-emerald-600">{perfLevels.highPerformers?.percentage || 0}%</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{perfLevels.performers?.count || 0}</p>
                <p className="text-sm text-slate-500">Performers</p>
                <p className="text-xs text-blue-600">{perfLevels.performers?.percentage || 0}%</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{perfLevels.nonPerformers?.count || 0}</p>
                <p className="text-sm text-slate-500">Non Performers</p>
                <p className="text-xs text-red-600">{perfLevels.nonPerformers?.percentage || 0}%</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <Activity className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {summary.minScore || '-'} - {summary.maxScore || '-'}
                </p>
                <p className="text-sm text-slate-500">Plage de scores</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Score distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="w-5 h-5 text-slate-500" />
              Distribution des scores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScoreDistributionBar distribution={scoreDistribution} />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="employees" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Employés
            </TabsTrigger>
            <TabsTrigger value="departments" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Départements
            </TabsTrigger>
          </TabsList>

          {/* Overview tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* High performers */}
              <Card className="border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
                    <Star className="w-5 h-5" />
                    Top High Performers
                  </CardTitle>
                  <CardDescription>Score ≥ 85/100</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {perfLevels.highPerformers?.employees?.length > 0 ? (
                    perfLevels.highPerformers.employees.map((emp) => (
                      <EmployeePerformanceCard
                        key={emp.id}
                        employee={emp}
                        level="high_performer"
                        onClick={() => navigate(`/skills/employee/${emp.id}`)}
                      />
                    ))
                  ) : (
                    <p className="text-center text-slate-500 py-4">Aucun high performer</p>
                  )}
                </CardContent>
              </Card>

              {/* Non performers */}
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Non Performers - Attention requise
                  </CardTitle>
                  <CardDescription>Score &lt; 60/100</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {perfLevels.nonPerformers?.employees?.length > 0 ? (
                    perfLevels.nonPerformers.employees.map((emp) => (
                      <EmployeePerformanceCard
                        key={emp.id}
                        employee={emp}
                        level="non_performer"
                        onClick={() => navigate(`/skills/employee/${emp.id}`)}
                      />
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <Award className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
                      <p className="text-emerald-600 font-medium">Aucun non performer !</p>
                      <p className="text-sm text-slate-500">Excellente performance globale</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Employees tab */}
          <TabsContent value="employees" className="mt-6">
            <div className="grid md:grid-cols-3 gap-4">
              {/* High performers list */}
              <Card>
                <CardHeader className="pb-2 bg-emerald-50">
                  <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
                    <Star className="w-4 h-4" />
                    High Performers ({perfLevels.highPerformers?.count || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 max-h-96 overflow-y-auto">
                  {perfLevels.highPerformers?.employees?.map((emp) => (
                    <div
                      key={emp.id}
                      className="flex items-center justify-between p-2 hover:bg-emerald-50 rounded cursor-pointer"
                      onClick={() => navigate(`/skills/employee/${emp.id}`)}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{emp.name}</p>
                        <p className="text-xs text-slate-500 truncate">{emp.jobTitle}</p>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700 ml-2">{emp.score}</Badge>
                    </div>
                  ))}
                  {!perfLevels.highPerformers?.employees?.length && (
                    <p className="text-center text-slate-400 py-4 text-sm">Aucun</p>
                  )}
                </CardContent>
              </Card>

              {/* Performers list */}
              <Card>
                <CardHeader className="pb-2 bg-blue-50">
                  <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                    <TrendingUp className="w-4 h-4" />
                    Performers ({perfLevels.performers?.count || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 max-h-96 overflow-y-auto">
                  <p className="text-center text-slate-400 py-4 text-sm">
                    Voir le détail dans la campagne
                  </p>
                </CardContent>
              </Card>

              {/* Non performers list */}
              <Card>
                <CardHeader className="pb-2 bg-red-50">
                  <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    Non Performers ({perfLevels.nonPerformers?.count || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 max-h-96 overflow-y-auto">
                  {perfLevels.nonPerformers?.employees?.map((emp) => (
                    <div
                      key={emp.id}
                      className="flex items-center justify-between p-2 hover:bg-red-50 rounded cursor-pointer"
                      onClick={() => navigate(`/skills/employee/${emp.id}`)}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{emp.name}</p>
                        <p className="text-xs text-slate-500 truncate">{emp.jobTitle}</p>
                      </div>
                      <Badge className="bg-red-100 text-red-700 ml-2">{emp.score}</Badge>
                    </div>
                  ))}
                  {!perfLevels.nonPerformers?.employees?.length && (
                    <p className="text-center text-slate-400 py-4 text-sm">Aucun</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Departments tab */}
          <TabsContent value="departments" className="mt-6">
            {byDepartment.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {byDepartment.map((dept) => (
                  <DepartmentCard key={dept.id || dept.name} dept={dept} />
                ))}
              </div>
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Aucune donnée par département</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Empty state */}
        {!reportData && (
          <Card className="py-12">
            <CardContent className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Aucune donnée disponible
              </h3>
              <p className="text-slate-500 mb-4">
                Sélectionnez une campagne pour voir les statistiques de performance
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
